import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../hooks/useRBAC';
import { usePrivateDealAssignment } from '../hooks/usePrivateDealAssignment';
import UserSelect from './UserSelect';

const getAssignedUserId = (risk) => {
    if (!risk) return null;
    return risk.assignedToUserId
        || risk.enterpriseAssignedToUserId
        || risk.ransomwareAssignedToUserId
        || risk.darkWebAssignedToUserId
        || risk.aiAssignedToUserId
        || risk.tprmAssignedToUserId
        || risk.businessUnitAssignedToUserId
        || null;
};

// dealId / dealIsPrivate: optional deal context for private-deal assignment rules.
// When the risk is linked to a private deal, non-owner/admin assigners only see deal
// members; the deal's owner/admin sees everyone (non-members tagged) and can grant
// Contributor access on the fly. Callers without deal context omit both — unchanged.
const AssignRiskModal = ({ show, onClose, risk, refreshData, assignEndpoint, riskId, riskType, onAssignSuccess, dealId, dealIsPrivate }) => {
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [targetDate, setTargetDate] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [lastInviteData, setLastInviteData] = useState(null);

    const apiCall = useApi();
    const { user } = useAuth();
    const { canManageOrganizationRisks, hasSelfScopeRole } = useRBAC();

    // Mirror the backend CanInviteUser gate (org-scope RISK_MANAGER+ OR self-scope RISK_MANAGER+).
    // Users who can't invite (e.g. self-scope contributors reassigning their own risk) shouldn't see
    // the "+" — POST /userInvite/invite would 403 for them.
    const canInviteAssignee = canManageOrganizationRisks() || hasSelfScopeRole('RISK_MANAGER');

    // Private-deal assignment rules (inert when no dealId / deal is public)
    const { isPrivateDeal, memberIds, canManagePrivateDeal, grantDealContributor } =
        usePrivateDealAssignment(show, dealId, dealIsPrivate);

    // Members-only picker for assigners who aren't the deal's owner/admin; owners/admins
    // see everyone with non-members tagged in the dropdown.
    const visibleUsers = (isPrivateDeal && !canManagePrivateDeal && memberIds)
        ? users.filter(u => memberIds.has(u.id))
        : users;
    const nonMemberIds = (isPrivateDeal && canManagePrivateDeal && memberIds)
        ? new Set(users.filter(u => !memberIds.has(u.id)).map(u => u.id))
        : undefined;
    // On a private deal only the owner/admin may assign by invite (backend 400s otherwise)
    const canInviteHere = canInviteAssignee && (!isPrivateDeal || canManagePrivateDeal);

    useEffect(() => {
        if (show) {
            fetchUsers();
            setSuccessMessage('');
            setErrorMessage('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    // Pre-populate when modal opens and users are loaded
    useEffect(() => {
        if (!show || !risk) {
            setSelectedUserId('');
            setTargetDate('');
            return;
        }
        if (users.length === 0) return;

        const assignedId = getAssignedUserId(risk);

        if (assignedId) {
            const matchingUser = users.find(u => u.id === assignedId);
            if (matchingUser) {
                setSelectedUserId(String(assignedId));
            }
        }
    }, [show, risk, users]);

    const fetchUsers = async () => {
        try {
            const res = await apiCall('GET', '/users');
            setUsers(res || []);
        } catch (err) {} finally {
            setLoadingUsers(false);
        }
    };

    const today = new Date().toISOString().split('T')[0];

    const handleSave = async (e) => {
        e.preventDefault();
        setIsProcessing(true);
        setErrorMessage('');
        setSuccessMessage('');

        // Resolve assignee display name for the confirmation message
        const isInvite = String(selectedUserId).startsWith('invite:');
        let assigneeName;
        if (isInvite && lastInviteData) {
            assigneeName = lastInviteData.fullName || lastInviteData.email || `User #${selectedUserId}`;
        } else {
            const found = users.find(u =>
                String(u.userId) === String(selectedUserId) ||
                String(u.id) === String(selectedUserId)
            );
            assigneeName = found
                ? (found.fullName || found.email || `User #${selectedUserId}`)
                : `User #${selectedUserId}`;
        }

        // All risks live in UnifiedRisks (keyed by the GUID id); the unified assign endpoint
        // expects the generic userId/userInviteId shape.
        const payload = {
            userId: isInvite ? null : (parseInt(selectedUserId) || null),
            userInviteId: isInvite ? parseInt(String(selectedUserId).replace('invite:', '')) : null,
            remediationTargetDate: targetDate ? new Date(targetDate).toISOString() : null
        };

        try {
            // Private deal + owner/admin assigning a non-member: confirm, then grant
            // CONTRIBUTOR@DEAL before the assignment (the backend rejects non-members).
            if (isPrivateDeal && canManagePrivateDeal && !isInvite && memberIds
                && !memberIds.has(parseInt(selectedUserId))) {
                const confirmed = window.confirm(
                    `${assigneeName} can't see this private deal. Grant Contributor access and assign?`
                );
                if (!confirmed) {
                    setIsProcessing(false);
                    return;
                }
                await grantDealContributor(parseInt(selectedUserId));
            }

            // All mutations route to the unified controller (keyed by the GUID).
            // Fall back to a provided assignEndpoint if no id is available.
            const endpoint = (riskId || risk.id)
                ? `/unifiedrisks/${riskId || risk.id}/assign`
                : assignEndpoint;

            // Write activity log FIRST, before the reassign persists. The log endpoint authorizes
            // non-managers via CanWriteRiskAsync, which checks assignment against the risk's CURRENT
            // assignee — so a contributor reassigning their own risk only passes while they are still
            // the assignee. Logging after the PUT would 403 (and be silently dropped) for that case.
            // Best-effort: its own catch so a log failure never blocks the assign.
            if (riskId && riskType) {
                try {
                    const prevAssigneeId = risk?.remediationAssignTo || getAssignedUserId(risk);
                    const isFirstAssign = !prevAssigneeId;
                    let prevAssigneeName = null;
                    if (!isFirstAssign && prevAssigneeId) {
                        const prevUser = users.find(u =>
                            String(u.userId) === String(prevAssigneeId) ||
                            String(u.id) === String(prevAssigneeId)
                        );
                        prevAssigneeName = prevUser
                            ? (prevUser.fullName || prevUser.email || `User #${prevAssigneeId}`)
                            : `User #${prevAssigneeId}`;
                    }
                    await apiCall('POST', '/allRemediations/logs/by-risk', {
                        remediationType: riskType,
                        riskId,
                        previousStatus: isFirstAssign ? null : prevAssigneeName,
                        newStatus: assigneeName,
                        comment: isFirstAssign
                            ? `Risk assigned to ${assigneeName}`
                            : `Risk reassigned to ${assigneeName}`,
                        changedByUserName: user?.userName || user?.email || '',
                        changeType: isFirstAssign ? 'Assign' : 'Reassign',
                        targetDate: targetDate ? new Date(targetDate).toISOString() : null,
                    });
                } catch (logErr) {}
            }

            await apiCall('PUT', endpoint, payload);

            // Immediately notify parent so it can optimistically update the risk row
            if (onAssignSuccess) {
                onAssignSuccess({
                    riskId,
                    userId: isInvite ? null : (parseInt(selectedUserId) || null),
                    userName: assigneeName,
                });
            }

            setSuccessMessage(`Risk assigned: ${assigneeName}`);
            refreshData();
            setTimeout(() => onClose(), 3000);
        } catch (err) {
            setErrorMessage('Failed to assign risk. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-content-primary">
                        Assign Risk: {risk.riskName}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSave} className="space-y-4">
                    {/* Dropdown */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assign To
                        </label>
                        <UserSelect
                            value={selectedUserId}
                            onChange={setSelectedUserId}
                            users={visibleUsers}
                            loading={loadingUsers}
                            canInvite={canInviteHere}
                            nonMemberIds={nonMemberIds}
                            onInviteSuccess={(inviteData) => setLastInviteData(inviteData)}
                        />
                        {isPrivateDeal && !canManagePrivateDeal && canInviteAssignee && (
                            <p className="text-xs text-gray-500 mt-1">
                                Only the deal owner can invite external users to a private deal.
                            </p>
                        )}
                        {isPrivateDeal && canManagePrivateDeal && canInviteAssignee && (
                            <p className="text-xs text-gray-500 mt-1">
                                Invited users will be granted access to this private deal when they join.
                            </p>
                        )}
                    </div>

                    {/* Remediation Target Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Remediation Target Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={targetDate}
                            min={today}
                            onChange={(e) => setTargetDate(e.target.value)}
                            className={`w-full border rounded-md px-3 py-2 ${!targetDate ? 'border-red-300' : 'border-gray-300'}`}
                        />
                        {!targetDate && (
                            <p className="text-xs text-red-500 mt-1">Required — past dates not allowed.</p>
                        )}
                    </div>

                    {/* Success / Error messages */}
                    {successMessage && (
                        <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                            {successMessage}
                        </div>
                    )}
                    {errorMessage && (
                        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                            {errorMessage}
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isProcessing || loadingUsers || !selectedUserId || !targetDate || targetDate < today || !!successMessage}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isProcessing ? "Assigning..." : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AssignRiskModal;
